<template>
  <el-drawer
      :visible="drawerVisible"
      size="65%"
      :with-header="false"
      custom-class="sales-trend-drawer"
      @open="onOpen"
      @close="handleClose"
      :destroy-on-close="true"
      :wrapperClosable="false"
  >
    <div class="sales-trend-container">

      <!-- 关闭按钮 -->
      <div class="drawer-close-btn" style="display: flex; align-items: center;">
        <el-popover
            placement="bottom"
            width="300"
            trigger="hover"
        >
          <div class="rule-content" style="padding: 10px; overflow-y: auto;">
            <strong>统计规则说明：</strong>
            <p>1. 销售数据仅统计已完成订单，不含退款、取消、审核等订单。</p>
            <p>2. 数据更新周期为每日凌晨，部分数据可能存在延迟。</p>
            <p>3. 综合日均销量采用月均法计算：统计所有时间总销量，除以涉及月份数，再除以30天，反映平均每日销售情况。</p>
            <p>4. 活跃客户数统计最近30天下单客户。</p>
            <p>5. 热销月份为销量TOP3月份。</p>
          </div>

          <template #reference>
            <el-button
                icon="el-icon-info"
                circle
                class="rule-btn"
                aria-label="规则说明"
                style="margin-right: 12px; background-color: #409EFF; color: white; border: none;"
            ></el-button>
          </template>
        </el-popover>

        <el-button
            icon="el-icon-close"
            circle
            @click="handleClose"
            aria-label="关闭"
            style="background-color: #f56c6c; color: white; border: none;"
        ></el-button>
      </div>


      <!-- 产品基本信息区 -->
      <div class="product-base-info">
        <div class="product-image-container">
          <el-image
              :src="productInfo.picture || defaultImg"
              class="product-image"
              fit="cover"
              :preview-src-list="[productInfo.picture || defaultImg]"
          >
            <div slot="error" class="image-slot">
              <i class="el-icon-picture-outline"></i>
            </div>
          </el-image>
        </div>
        <div class="product-details">
          <h3 class="product-name">{{ productInfo.productName || '产品名称' }}</h3>
          <div class="product-meta">
            <div class="meta-item">
              <span class="meta-label">分类：</span>
              <span class="meta-value">{{ productInfo.productClassifyName || '-' }}</span>
            </div>
            <div class="meta-item">
              <span class="meta-label">规格：</span>
              <span class="meta-value">{{ productInfo.spec }}</span>
            </div>
            <div class="meta-item">
              <span class="meta-label">价格：</span>
              <span class="meta-value">¥{{ productInfo.price || '0.00' }}</span>
            </div>
            <div class="meta-item">
              <span class="meta-label">成本价：</span>
              <span class="meta-value">¥{{ productInfo.costPrice }}</span>
            </div>
            <div class="meta-item">
              <span class="meta-label">创建时间：</span>
              <span class="meta-value">{{ productInfo.createTime }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- 数据概览卡片 -->
      <div class="data-overview-cards-v2">
        <!-- 1. 历史总销量 -->
        <div class="stat-card sales">
          <div class="card-header">
            <i class="el-icon-sold-out"></i>
            <span>累计总销量</span>
          </div>
          <div class="card-value">
            <count-to :end-val="statsData.totalSales" :duration="1000" />
            <span class="unit">箱</span>
          </div>
          <div class="card-footer">产品历史总销量</div>
        </div>

        <!-- 2. 历史总销售额 -->
        <div class="stat-card revenue">
          <div class="card-header">
            <i class="el-icon-money"></i>
            <span>累计总销售额</span>
          </div>
          <div class="card-value">
            ¥<count-to :end-val="statsData.totalRevenue" :duration="1000" :decimals="2" />
            <span class="unit">元</span>
          </div>
          <div class="card-footer">产品历史总销售金额</div>
        </div>

        <!-- 3. 累计销售次数（新增迷你图表） -->
        <div class="stat-card count">
          <div class="card-header">
            <i class="el-icon-tickets"></i>
            <span>累计销售次数</span>
          </div>
          <div class="card-content">
            <div class="card-value">
              <count-to :end-val="statsData.totalSalesCount" :duration="1000" />
              <span class="unit">次</span>
            </div>
            <div class="mini-chart" ref="salesCountChart"></div>
          </div>
          <div class="card-footer">产品总销售次数</div>
        </div>

        <!-- 4. 综合日均销量 -->
        <div class="stat-card daily">
          <div class="card-header">
            <i class="el-icon-data-line"></i>
            <span>综合日均销量</span>
          </div>
          <div class="card-value">
            <count-to :end-val="statsData.avgDailySales" :duration="1000" />
            <span class="unit">箱/天</span>
          </div>
          <div class="card-footer">基于销售天数</div>
        </div>

        <!-- 5. 年度同比变化 -->
        <div class="dashboard-stats">
          <!-- 年度同比变化 -->
          <div class="stat-card yoy" :class="parseTrend(statsData.yoy).type">
            <div class="card-header">
              <i class="el-icon-s-data"></i>
              <span>年度同比变化</span>
            </div>
            <div class="card-value">
              {{ parseTrend(statsData.yoy).text }}
              <count-to
                  v-if="parseTrend(statsData.yoy).value !== 0"
                  :end-val="parseTrend(statsData.yoy).value"
                  :duration="1000"
              />%
              <i :class="parseTrend(statsData.yoy).icon"></i>
            </div>
            <div class="card-footer">对比去年同期段</div>
          </div>
          </div>

        <!-- 6. 热销月份分析 -->
        <div class="stat-card hot">
          <div class="card-header">
            <i class="el-icon-sunny"></i>
            <span>热销月份</span>
          </div>
          <div class="card-value">
            {{ statsData.hotMonths}}
          </div>
          <div class="card-footer">销量TOP3月份</div>
        </div>

        <!-- 7. 活跃客户数 -->
        <div class="stat-card clients">
          <div class="card-header">
            <i class="el-icon-user-solid"></i>
            <span>活跃客户数</span>
          </div>
          <div class="card-value">
            <count-to :end-val="statsData.activeClients" :duration="1000" />
            <span class="unit">人</span>
          </div>
          <div class="card-footer">近30天下单客户</div>
        </div>

        <!-- 8. 平均成交价 -->
        <div class="stat-card price">
          <div class="card-header">
            <i class="el-icon-price-tag"></i>
            <span>平均成交价</span>
          </div>
          <div class="card-value">
            ¥<count-to :end-val="statsData.avgDealPrice" :duration="1000" :decimals="2" />
            <span class="unit">箱/元</span>
          </div>
          <div class="card-footer">产品销售平均成交价</div>
        </div>

        <!-- 9. 近期销售趋势 -->
        <!-- 近期销售趋势 -->
          <!-- 近期销售趋势 -->
          <div class="stat-card trend" :class="parseTrend(statsData.trend).type">
            <div class="card-header">
              <i class="el-icon-s-marketing"></i>
              <span>近期销售趋势</span>
            </div>
            <div class="card-value">
              {{ parseTrend(statsData.trend).text }}
              <count-to
                  v-if="parseTrend(statsData.trend).value !== 0"
                  :end-val="parseTrend(statsData.trend).value"
                  :duration="1000"
              />%
              <i :class="parseTrend(statsData.trend).icon"></i>
            </div>
            <div class="card-footer">近3个月销售情况变化</div>
          </div>
      </div>
      <!-- 数据筛选控制区 -->
      <div class="data-controls">
        <trend-chart-switcher :productId="productId" />
      </div>

      <!-- 客户分析表格 -->
      <el-card class="customer-analysis" shadow="hover" v-loading="loading">
        <div slot="header" class="table-header">
          <span>客户购买分析</span>
       <!--   <el-button size="mini" type="primary" plain @click="exportTable">导出数据</el-button>-->
        </div>

        <el-table
            :data="customerData"
            style="width: 100%"
           height="410"
            border
            :default-sort="{ prop: 'totalAmount', order: 'descending' }"
        >
          <!-- 客户名称 -->
          <el-table-column prop="customerName" label="客户名称" width="150" />

          <!-- 购买次数 -->
          <el-table-column prop="purchaseCount" label="购买次数" width="90">
            <template slot-scope="scope">
              <span>{{ formatNumber(scope.row.purchaseCount || 0) }}</span>
            </template>
          </el-table-column>

          <!-- 购买总量 -->
          <el-table-column prop="totalQuantity" label="购买总量(箱)" sortable width="150">
            <template slot-scope="scope">
              <span>{{ formatNumber(scope.row.totalQuantity || 0) }}</span>
            </template>
          </el-table-column>

          <!-- 总金额 -->
          <el-table-column prop="totalAmount" label="总金额(元)" sortable width="160">
            <template slot-scope="scope">
              <span>¥{{ formatNumber(scope.row.totalAmount || 0) }}</span>
            </template>
          </el-table-column>

          <!-- 平均单价 -->
          <el-table-column prop="avgPrice" label="平均成交价(元)" width="130">
            <template slot-scope="scope">
              <span v-if="scope.row.avgPrice !== null">¥{{ formatNumber(scope.row.avgPrice) }}</span>
              <span v-else>-</span>
            </template>
          </el-table-column>

          <!-- 金额占比 -->
          <el-table-column prop="percentage" label="金额占比" align="center" sortable>
            <template slot-scope="scope">
              <el-progress
                  v-if="scope.row.percentage !== null"
                  :percentage="parseFloat(scope.row.percentage.toFixed(2))"
                  :color="getProgressColor"
                  :stroke-width="21"
                  :format="formatProgress"
                  text-inside
              />
              <span v-else>-</span>
            </template>
          </el-table-column>
        </el-table>
      </el-card>

    </div>

  </el-drawer>
</template>



<script>
import CountTo from 'vue-count-to'
import TrendChartSwitcher from './TrendChartSwitcher.vue'
export default {
  name: 'ProductSalesTrendDrawer',
  components: {
    CountTo,
    TrendChartSwitcher
  },
  model: {
    prop: 'drawerVisible',
    event: 'update:drawerVisible'
  },
  props: {
    drawerVisible: {
      type: Boolean,
      required: true
    },
    productId: Number,
  },
  data() {
    const currentDate = new Date()
    const currentYear = currentDate.getFullYear()
    const currentMonth = String(currentDate.getMonth() + 1).padStart(2, '0')
    const lastYearDate = new Date()
    lastYearDate.setFullYear(currentYear - 1)
    const lastYearMonth = String(lastYearDate.getMonth() + 1).padStart(2, '0')

    return {

      // 对比图只显示销量或金额，默认显示销量
      compareMetric: 'number', // 可选 'number' 或 'amount'
      // 图表实例
      mainChart: null,
      contrastChart: null,
      chartMain: null,
      chartCompare: null,

      productInfo: {
        productName: '',
        picture: '',
        productClassifyName: '',
        volume: 0,
        abv: 0,
        price: 0,
        stock: 0
      },
      trendData:[],
      statsData: {
        totalSales: 0,
        totalRevenue: 0,
        avgDailySales: 0,
        yoy: 0,
        hotMonths: "",
        activeClients: 0,
        avgDealPrice: 0,
        trend: 0
      },
      defaultImg: 'https://www.kongqichong.com:9000/file/2024/03/10/4ca95bb059a54904801ac4cfe7716a32.jpg',
      reportType: 'year',
      yearValue: currentYear.toString(),
      timeValue: `${currentYear}-${currentMonth}`,
      compareYearValue: (currentYear - 1).toString(),
      compareTimeValue: `${currentYear - 1}-${lastYearMonth}`,
      enableCompare: false,
      chart: null,
      salesData: [],
      compareData: [],
      loading: false,
      customerData: [],
      customColors: [
        { color: '#f56c6c', percentage: 20 },
        { color: '#e6a23c', percentage: 40 },
        { color: '#5cb87a', percentage: 60 },
        { color: '#1989fa', percentage: 80 },
        { color: '#6f7ad3', percentage: 100 }
      ]
    }
  },
  // watch: {
  //   productId: {
  //     immediate: true,
  //     handler(newVal) {
  //       if (newVal) {
  //         this.fetchProductDetail()
  //       }
  //     }
  //   }
  // },
  mounted() {
    window.addEventListener('resize', this.handleResize)

  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
    if (this.chart) {
      this.chart.dispose()
    }
  },
  methods: {
    formatProgress(percentage) {
      return `${percentage.toFixed(2)}%`; // 保留两位小数
    },
    getProgressColor(percentage) {
      if (percentage >= 80) return '#67C23A'; // 绿色
      if (percentage >= 50) return '#E6A23C'; // 橙色
      return '#F56C6C'; // 红色
    },
    parseTrend(trendStr) {
      // 类型检查，确保是字符串
      if (typeof trendStr !== 'string') {
        trendStr = String(trendStr);
      }

      // 初始化返回对象
      const result = {
        text: '无数据',
        value: 0,
        type: '',
        icon: ''
      };

      // 处理空字符串
      if (!trendStr.trim()) {
        return result;
      }

      // 判断趋势类型
      if (trendStr.includes('上升')) {
        result.text = '上升';
        result.type = 'positive';
        result.icon = 'el-icon-top';
      } else if (trendStr.includes('下降')) {
        result.text = '下降';
        result.type = 'negative';
        result.icon = 'el-icon-bottom';
      } else if (trendStr.includes('持平')) {
        result.text = '持平';
        result.type = 'neutral';
      } else {
        // 未知格式，尝试提取数值
        result.text = '变化';
      }

      // 提取数值部分
      const numMatch = trendStr.match(/\d+\.?\d*/);
      if (numMatch) {
        result.value = parseFloat(numMatch[0]);
      }

      return result;
    },

    onOpen() {
      this.$nextTick(() => {
       this.fetchProductDetail();//产品信息
        this.fetchProductAnalysis();//数据分析
        this.fetchCustomerAnalysis()
      })
    },

    async fetchProductDetail() {
      try {
        const res = await this.$http.get('/alcohol-sales/productDetail', {
          params: { productId: this.productId }
        })
        this.productInfo=res.data.resultObj
      } catch (error) {
        this.$message.error('获取产品详情失败')
      }
    },
    async fetchProductAnalysis() {
      try {
         const res = await this.$http.get('/alcohol-sales/product-analysis', {
          params: { productId: this.productId }
         })
        this.statsData = res.data.resultObj
      } catch (error) {
        console.error('获取产品分析数据失败:', error)
      }
    },

    /**
     * 获取客户分析数据 - 暂用模拟数据
     */
    async fetchCustomerAnalysis() {
      this.loading = true
      try {
        //实际接口准备好后取消注释
        const res = await this.$http.get('/alcohol-sales/product-customer', {
          params: { productId: this.productId }
        })
        this.customerData = res.data.resultObj

        // 开发阶段使用模拟数据

      } catch (error) {
        console.error('获取客户数据失败:', error)

      } finally {
        this.loading = false
      }
    },

    /**
     * 关闭抽屉
     */
    handleClose() {
      this.$emit('update:drawerVisible', false)
    },

    /**
     * 格式化数字显示千分位
     */
    formatNumber(num) {
      return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")
    },

    /**
     * 查看客户详情
     */
    viewCustomerDetail(row) {
      this.$message.info(`查看客户 ${row.customerName} 的详细购买记录`)
    },

    /**
     * 导出表格数据
     */
    exportTable() {
      this.$message.success('导出数据成功')
    }
  }
}
</script>

<style lang="scss" scoped>



/* 整体 Drawer 容器 */
.sales-trend-drawer {
  font-family: 'Helvetica Neue', Helvetica, 'PingFang SC', 'Hiragino Sans GB',
  'Microsoft YaHei', Arial, sans-serif;
  padding: 0;
  background: #f8fafc;
  box-shadow: -5px 0 15px rgba(0, 0, 0, 0.05);
  border-radius: 12px 0 0 12px;
  overflow: hidden;
}

.sales-trend-container {
  height: 100%;
  overflow-y: auto;
  padding: 24px;
  background: linear-gradient(135deg, #f8fafc 0%, #f0f4f8 100%);
}

/* 关闭按钮 */
.drawer-close-btn {
  position: absolute;
  right: 28px;
  top: 28px;
  z-index: 10;
}

.drawer-close-btn .el-button {
  width: 36px;
  height: 36px;
  background: white;
  border: 1px solid #e4e7ed;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.drawer-close-btn .el-button:hover {
  transform: rotate(90deg) scale(1.1);
  background: #f56c6c;
  color: white;
  border-color: transparent;
}

/* 产品信息区域 */
.product-base-info {
  display: flex;
  align-items: flex-start;
  background: white;
  border-radius: 16px;
  padding: 24px 28px;
  margin-bottom: 28px;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.04);
  border: none;
  position: relative;
  gap: 24px;
  overflow: hidden;
}

.product-base-info::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 6px;
  height: 100%;
  background: linear-gradient(to bottom, #409eff, #67c23a);
}

.product-image-container {
  width: 140px;
  height: 140px;
  padding: 10px;
  background: #fff;
  border-radius: 12px;
  border: 1px solid rgba(64, 158, 255, 0.15);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.3s;
}

.product-image-container:hover {
  transform: scale(1.05);
}

.product-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 8px;
}

.image-slot {
  font-size: 28px;
  color: #ccc;
}

.product-details {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;

  .product-name {
    font-size: 22px;
    font-weight: 700;
    margin: 0 0 18px;
    color: #303133;
  }

  .product-meta {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px 28px;

    .meta-item {
      display: flex;
      line-height: 1.6;
      font-size: 14px;
      align-items: baseline;
    }

    .meta-label {
      color: #909399;
      font-weight: 500;
      width: 80px;
      min-width: 60px;
      display: inline-block;
    }

    .meta-value {
      color: #303133;
      font-weight: 600;
      word-break: break-word;
      flex: 1;
    }
  }
}

/* 数据卡片区域 */
.data-overview-cards-v2 {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 20px;
  margin-bottom: 28px;
}

.stat-card {
  background: white;
  border-radius: 12px;
  padding: 22px;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.03);
  border: none;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
}

.stat-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.08);
}

.card-header {
  display: flex;
  align-items: center;
  margin-bottom: 16px;
  color: #606266;
  font-size: 15px;
  font-weight: 500;
  position: relative;
  z-index: 2;
}

.card-header i {
  font-size: 28px;
  margin-right: 14px;
  width: 52px;
  height: 52px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 14px;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.3) 0%, rgba(255, 255, 255, 0) 100%);
  position: relative;
  transition: all 0.3s ease;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.05);
}

.card-header i::after {
  content: '';
  position: absolute;
  width: 44px;
  height: 44px;
  border-radius: 10px;
  background: currentColor;
  opacity: 0.12;
  z-index: 0;
}

.card-header i.el-icon {
  position: relative;
  z-index: 1;
  color: inherit;
}

/* 图标专属颜色 */
.stat-card.sales .card-header i { color: #67c23a; }
.stat-card.revenue .card-header i { color: #e6a23c; }
.stat-card.count .card-header i { color: #8a2be2; }
.stat-card.daily .card-header i { color: #20b2aa; }
.stat-card.yoy .card-header i { color: #409eff; }
.stat-card.hot .card-header i { color: #f56c6c; }
.stat-card.clients .card-header i { color: #ff9900; }
.stat-card.price .card-header i { color: #9966ff; }
.stat-card.trend .card-header i { color: #ff6666; }

/* 卡片内容 */
.card-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.card-value {
  font-size: 26px;
  font-weight: 600;
  color: #303133;
  margin: 8px 0;
  display: flex;
  align-items: center;
  font-family: 'Arial Rounded MT Bold', 'Helvetica Rounded', Arial, sans-serif;
}

.card-value .unit {
  font-size: 15px;
  color: #909399;
  margin-left: 6px;
  font-weight: normal;
}

.card-footer {
  font-size: 13px;
  color: #909399;
  margin-top: auto;
  opacity: 0.8;
}

/* 数据控制区 */
.data-controls {
  background: white;
  border-radius: 12px;
  padding: 20px 24px;
  margin-bottom: 28px;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.03);
  border: none;
}

.control-group {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 14px;
}

/* 图表 */
.chart-container {
  border-radius: 12px;
  margin-bottom: 28px;
  border: none;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.03);
  background: white;
  overflow: hidden;
}

/* 客户分析表格 */
.customer-analysis {
  border-radius: 12px;
  border: none;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.03);
  overflow: hidden;
  max-height: 460px;
}

.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 18px 24px;
  background: white;
  border-bottom: 1px solid #f1f3f7;
}

/* 响应式支持 */
@media (max-width: 1200px) {
  .data-overview-cards-v2 {
    grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  }
}

@media (max-width: 768px) {
  .product-base-info {
    flex-direction: column;
    align-items: center;
    text-align: center;
  }

  .product-image-container {
    margin-bottom: 20px;
  }

  .product-details {
    align-items: center;

    .product-meta {
      grid-template-columns: 1fr !important;
    }

    .product-name {
      font-size: 18px;
    }
  }

  .data-overview-cards-v2 {
    grid-template-columns: 1fr 1fr;
  }

  .card-header i {
    width: 44px;
    height: 44px;
    font-size: 24px;
  }
}

@media (max-width: 576px) {
  .data-overview-cards-v2 {
    grid-template-columns: 1fr;
  }

  .sales-trend-container {
    padding: 16px;
  }
}

/* 动效 */
@keyframes float {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-5px); }
}

.stat-card:hover .card-header i {
  animation: float 1.5s ease-in-out infinite;
}

.positive .card-header i {
  animation: pulse 2s ease infinite;
}

@keyframes pulse {
  0% { box-shadow: 0 0 0 0 rgba(103, 194, 58, 0.4); }
  70% { box-shadow: 0 0 0 10px rgba(103, 194, 58, 0); }
  100% { box-shadow: 0 0 0 0 rgba(103, 194, 58, 0); }
}



</style>



// WEBPACK FOOTER //
// ProductSalesTrend.vue?73015afb